/**
 * Branding Provider
 *
 * Provides updatedAt via React context so logo URLs trigger
 * re-renders after the one-shot refreshBranding() completes.
 */
import { createContext, useContext, useEffect, useState } from "react";
import type { ConvexReactClient } from "convex/react";
import { refreshBranding } from "./runtimeBranding";
import { getConvexBaseUrl } from "./brandingUtils";

interface BrandingContextValue {
  updatedAt: number;
}

const BrandingContext = createContext<BrandingContextValue>({ updatedAt: 0 });

export function BrandingProvider({
  convex,
  children,
}: {
  convex: ConvexReactClient;
  children: React.ReactNode;
}) {
  const [updatedAt, setUpdatedAt] = useState<number>(
    () => (window as any).__jourfxBrandingUpdatedAt || 0
  );

  useEffect(() => {
    void refreshBranding(convex).then(() => {
      setUpdatedAt((window as any).__jourfxBrandingUpdatedAt || 0);
    });
  }, [convex]);

  return (
    <BrandingContext.Provider value={{ updatedAt }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBrandLogoUrl(
  kind: "primary" | "square" = "primary"
): string {
  const { updatedAt } = useContext(BrandingContext);
  const base = String(getConvexBaseUrl()).replace(/\/$/, "");
  const path =
    kind === "primary"
      ? "/api/branding/logo-primary"
      : "/api/branding/logo-square";
  return `${base}${path}?v=${updatedAt}`;
}
